<template>
  <div id="faqs" class="container container-height faq-page">
    <div class="faq-inner">
      <h1 class="animated-element page-title">FAQ'S</h1>

      <nav class="animated-element section-nav">
        <button
          v-for="section in sections"
          :key="section.id"
          class="section-nav-item"
          @click="scrollToSection(section.id)"
        >
          {{ section.title }}
        </button>
      </nav>

      <section
        v-for="section in sections"
        :key="section.id"
        :ref="(el) => (sectionRefs[section.id] = el)"
        class="faq-section"
      >
        <h2 class="animated-element section-title">{{ section.title }}</h2>

        <div
          v-for="(item, index) in section.items"
          :key="index"
          class="animated-element"
        >
          <!-- Separate element: Vue's :class re-render would wipe the observer's in-view class -->
          <div class="faq-item" :class="{ open: isOpen(section.id, index) }">
            <button
              class="faq-question"
              :aria-expanded="isOpen(section.id, index)"
              :aria-controls="`faq-${section.id}-${index}`"
              @click="toggle(section.id, index)"
            >
              <span>{{ item.question }}</span>
              <span class="faq-icon" aria-hidden="true"></span>
            </button>

            <div :id="`faq-${section.id}-${index}`" class="faq-answer" role="region">
              <div class="faq-answer-inner">
                <template v-for="(block, blockIndex) in item.answer" :key="blockIndex">
                  <p v-if="typeof block === 'string'" v-html="block"></p>
                  <h4 v-else-if="block.heading" class="answer-heading">
                    {{ block.heading }}
                  </h4>
                  <ul v-else-if="block.list">
                    <li v-for="(line, lineIndex) in block.list" :key="lineIndex" v-html="line"></li>
                  </ul>
                </template>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
const EMAIL = '<a href="mailto:hellohi@studiobutter.co">hellohi@studiobutter.co</a>';
const INSTAGRAM = '<a href="https://www.instagram.com/studio_butter_/" target="_blank">Instagram</a>';

export default {
  data() {
    return {
      openItems: {},
      sectionRefs: {},
      sections: [
        {
          id: "general",
          title: "General",
          items: [
            {
              question: "WHAT IS THIS PLACE?",
              answer: [
                "Hey y’all! We are Studio Butter. A multipurpose, photo & video studio in the Brixton/Stockwell area (SW9), owned and operated by husband & wife duo Matt Whitchurch and Sydney Sainté out of our home! We are both working actors with over 15+ years of experience in theatre, TV and film, in London, New York and Los Angeles.",
              ],
            },
            {
              question: "WHAT’S ON OFFER?",
              answer: [
                "Wouldn’t you like to know? Well, our current offerings include both self-tape and headshot sessions. Our incredibly versatile, fully kitted out space is also available for studio hire. Whether you’re in need of a place to shoot an editorial spread or simply do a pesky, last-minute commercial self-tape - we are here and ready to fulfil all your creative needs.",
              ],
            },
            {
              question: "WHEN ARE YOU OPEN?",
              answer: [
                "<strong>Self-tape Session Hours</strong> - Monday to Friday, 10:00-18:00; Saturday & Sunday, 11:00-17:00",
                "<strong>Headshot Session Hours</strong> - Tuesday to Friday, 10:00-18:00; Saturday, 11:00-16:00",
              ],
            },
            {
              question: "WHERE ARE YOU BASED?",
              answer: [
                "Our cozy, fully functional studio is located in Central South London, in between Oval (Northern line) and Stockwell (Victoria line) tube stations. As the studio is literally in our flat, obviously you’ll need to book to get the full address, bookie!",
              ],
            },
            {
              question: "CAN I DRIVE TO THE STUDIO? IS THERE PARKING?",
              answer: [
                "You may! Just give us a shout in advance if you'll be driving to us and we will send you a link for Pay-and-Display parking. On our road, parking is all residential and pretty religiously enforced. Pay-and-Display parking meter permits are £5/hour.",
              ],
            },
            {
              question: "IS THERE SOMEWHERE TO CHANGE OR WAIT?",
              answer: [
                "We’ve got you covered, baby. For headshot and self-tape sessions, you have access to both a green room and a full bathroom. In the green room, feel free to lounge on the daybed, make sure you look fab in our full-length mirrors, and use the hooks & hangers for all your headshot options and elaborate self-tape costumes. It also has a small balcony for a coffee/tea break or if you’re early or just if you fancy! “Our casa…” and all that jazz.",
              ],
            },
            {
              question: "CAN I SEE EXAMPLES OF YOUR WORK?",
              answer: [
                `You can and you must! We post our selects from all our headshot/portrait shoots along with stills from self-tapes sessions on our Website and on our ${INSTAGRAM}, both of which we update regularly.`,
              ],
            },
          ],
        },
        {
          id: "booking",
          title: "Booking",
          items: [
            {
              question: "HOW DO I BOOK MY HEADSHOT OR SELF-TAPE SESSION?",
              answer: [
                "Making an appointment with us couldn’t be simpler. The easiest way to book a headshot or portrait shoot or self-tape session, is to visit our website, Studiobutter.co. Once there, click on the <a href=\"/booking\">Booking</a> heading. That will take you to a menu of appointment schedules. Select the self-tape session or headshot pack you want, and it will take you to a page where you can choose your desired date and time for your booking. For self-tapes, you pay the session fee upon completion of the booking form. For headshots, you pay the deposit for your session upon completion of the booking form, full remittance is due 1-week before your shoot. We will then send you a booking confirmation email with the necessary information to prepare you for your visit.",
              ],
            },
            {
              question: "WHAT IF I DON’T SEE THE TIMESLOT I WANT?",
              answer: [
                `Sometimes the exact date or time you need might be unavailable. If the booking site reflects that your ideal timeslot is occupied, please, please, please email ${EMAIL} or DM us to double check. We might actually be booked & busy but where we can, we will try to shimmy you into the schedule.`,
              ],
            },
            {
              question: "CAN I BOOK LAST MINUTE?",
              answer: [
                "Are you trying to cram a self-tape audition with a draconian turnaround time into your already busy week? That’s cool! Clients can book taping sessions up to 4 hours before the desired timeslot. For headshots, the last-minute window is 72 hours or 3 days. All that to say, all cancellation policies apply, even when you book at the last minute.",
              ],
            },
            {
              question: "HOW LONG SHOULD I BOOK FOR MY SELF-TAPE SESSION?",
              answer: [
                "OKAY. The million-pound, constantly asked question! It depends; we generally suggest booking more time than you think is necessary. Nothing worse than paying for a self-tape session at a studio and running out of time! And we want you to get your money’s worth, doll. At the end of the day, ‘tis completely up to you, as you know how you work best. We will keep time as the session goes along and give you plenty of 5-min warnings so you don’t feel too rushed.",
                { heading: "A rough time guide:" },
                {
                  list: [
                    "1-3 pages of material = 60-mins of taping",
                    "3-5 pages of material = 90-mins of taping",
                    "&gt;6 pages of material = 120-mins of taping",
                  ],
                },
              ],
            },
            {
              question: "ARE THERE OUT-OF-HOURS RATES FOR SELF-TAPE SESSIONS?",
              answer: [
                "We are open for Self-Tapes, 7-days a week. That’s right, weekends are in-hours too! So, for self-tapes, Out-of-Hours for our studio means anything before 10:00 or after 18:00, Monday-Friday and anything before 11:00 or after 17:00 on Saturday and Sunday.",
                `Have OOH self-tape requests? Please email ${EMAIL} or DM to book. Rates are as follows:`,
                { list: ["£50 - 60-mins", "£65 - 90-mins", "£80 - 120-mins"] },
              ],
            },
            {
              question: "WHAT IF I NEED TO CANCEL?",
              answer: [
                "So! We are aware that cancellations sometimes have to happen. We completely understand. HOWEVER. When you book, you are making a commitment (and so are we!) to a specified date & time. If you cancel after a certain point, we lose the ability to make that slot available to other cherished clients. Simple as that. We won’t play with your time, and we politely ask that you not play with ours.",
                { heading: "For Headshots & Portraiture" },
                {
                  list: [
                    "Up to 1-week prior to appointment - full refund of session fee + deposit (we will keep the deposit if you decide to rebook your headshots simultaneously)",
                    "Up to 48-hours prior to appointment -",
                    "After 24 - no session fee OR deposit refund",
                  ],
                },
                { heading: "For Self-Tapes" },
                {
                  list: [
                    "Up to 48 hours before appointment - your choice of a full refund OR studio credit (30-day validity)",
                    "Up to 4 hours before appointment - studio credit only (30-day validity)",
                    "Less than 4 hours before appointment - no refund OR credit",
                  ],
                },
              ],
            },
          ],
        },
        {
          id: "self-tapes",
          title: "Self-Tapes",
          items: [
            {
              question: "DO YOU PROVIDE READERS FOR SELF-TAPES?",
              answer: [
                "We absolutely do! And very good ones, if we do say so ourselves. We are both working actors with plenty of experiences. For self-tape scenes, you will always either be working with both of us, or one of us. You can state your preference of reader gender but we cannot guarantee availability.",
              ],
            },
            {
              question: "DO YOU OFFER DIRECTION OR COACHING?",
              answer: [
                "Yes. Light direction and coaching. Emphasis on light, y’all. We will always ask how you prefer to work on your self-tapes; some folks come in with plenty of ideas to try out in a stress-free, playful environment while some feel more supported if they have a more of an outside eye on their scenes. All we ever aim to do is to bring the best out of you! Both of us are working actors with plenty of experience so either way, your tapes are in capable hands.",
              ],
            },
            {
              question: "WHAT IS THE SET-UP FOR SELF-TAPES?",
              answer: [
                "Giving you the goods! We shoot all self-tapes on our Fujifilm-XT3. Our gorgeous studio is well-equipped for all lighting and audio needs. The sound quality on our camera is fantastic but we choose to use Hollyland Lark Max 2 wireless mics; all the better to hear you with my dear! We have a versatile collection of continuous lighting to make sure casting is seeing you in the best light possible. We offer a choice of bespoke, hand-painted, canvas backdrops in various flattering colors, and our goal is always to make sure we nail the set-up so that all you have to worry about is locking in and giving a great performance.",
              ],
            },
            {
              question: "DO YOU EDIT MY SELF-TAPES?",
              answer: [
                "We’d prefer not to alter your brilliance, darling. That said, for self-tapes, most of the color correction and lighting adjustments will usually be done in-camera, while we frame up or as we go. Any and all post-production adjustments are on a case-by-case basis; if something is really distracting we will alter. Mostly, we add transitions, make sure the sound levels are good, compress them and that’s it! No filters necessary - after all, we want you to get the job based on who you are and what choices you make.",
              ],
            },
            {
              question: "HOW SHOULD I PREPARE FOR MY SELF-TAPE SESSION?",
              answer: [
                "Just like you would for an in-person meeting or audition! For film, tv and commercial work, please come as off-book as possible, with your sides handy (print outs or phone/tablet). It makes all the difference in terms of using your time to get the most out of your takes. For theatre self-tapes, we’d suggest being very familiar with the material but on-book. We ask folks to arrive 15-mins before their booking time, especially if you have wardrobe or hair/makeup needs - it helps for us to have a few moments to rehearse and chat about what your scenes need. Ideally, when it’s time to start the appointment, all we have to do is frame up, put on your mic and press record!",
              ],
            },
            {
              question: "WHAT HAPPENS IF I GO OVER MY BOOKED TIME?",
              answer: [
                "Very good question, friend! We ask folks to arrive 15-mins before their booking time for self-tapes. And that is usually enough to cover you and us on both ends. That said, while we would love to tell you that going over your booked self-tape session timeslot is impossible and that we are perfect timekeepers, alas…stuff happens. So, in the event that you do need extra time to get your self-tape done, we charge £15 per additional 30 mins. Subject to our discretion and studio availability, of course.",
              ],
            },
            {
              question: "WHAT IS THE TURNAROUND TIME FOR SELF-TAPES?",
              answer: [
                "We aim to send your favorite, edited, compressed takes within 1-2 hours of the end of your appointment. We guarantee the return of your self-tapes by the end of day. If you have a tight deadline, please let us know as far in advance as possible. We’ve even counseled clients who have self-tapes due imminently to factor in post-production time into their booking time!",
              ],
            },
            {
              question: "HOW DO YOU SEND ME MY SELF-TAPES?",
              answer: [
                "We usually send via WeTransfer or Dropbox. We are open to working with whatever way you’d like to receive your self-tapes! We never submit on behalf of clients, however. It’s up to y’all to take it over the finish line!",
              ],
            },
            {
              question: "WHAT HAPPENS AFTER?",
              answer: [
                "After our work is done, we delete your work, baby! We might ask to use a still or a small clip (without audio) of your self-tape for the odd social media post, but typically we bin them the very same day. Please do keep us updated on how it goes after submission - did your agent like the finished product, did you get a recall, did you BOOK IT?!??? Tell us. It means the world to us.",
              ],
            },
          ],
        },
        {
          id: "headshots",
          title: "Headshots",
          items: [
            {
              question: "WHAT HEADSHOT OR PORTRAIT PACK WOULD BE BEST FOR ME?",
              answer: [
                "<strong>THE ACTOR PACK</strong> - The essentials package. Entirely digital shoot, 3-5 looks, 4 final prints. Professional, cost effective, does the job and does it well!",
                "<strong>THE POET PACK</strong> - We’d call the Business class of headshot packages. You get the best of both film and digital worlds, for a slightly higher price point. Same 4 final prints, but with an added editorial edge.",
                "<strong>THE WORKS PACK</strong> - This package is for the client who needs it all-in-one. Film, digital, headshots AND portraits. A 3-hour session, a whopping 6 final prints.",
              ],
            },
            {
              question: "WHAT IS THE DIFFERENCE BETWEEN DIGITAL & FILM HEADSHOTS?",
              answer: [
                "<strong>The Deal With Digital</strong> - We love digital headshots. Digital headshots are the classic choice. Other than the obvious, the main difference is ease. Digital cameras are efficient, and versatile! The control you have over the finished product is unparalleled. Digital shots make absolutely perfect headshots and are industry standard.",
                "<strong>Film is King</strong> - A film shoot is a whole different kettle of fish. Film makes your headshots vivid and cinematic; headshots are an actor’s calling card, with a film shoot you’re getting a unique upgrade to the norm. We use both 35mm film and medium format film - both boast a versatile and striking finished product.",
              ],
            },
            {
              question: "WHAT DO YOU SHOOT ON?",
              answer: [
                "For digital shoots, our tried and trusty Fujifilm-XT3. For film, we have a few options! Mostly, for 35mm we use our Minolta x700. For medium format our Mamiya C330 or our Yashica Mat. We have a gorgeous collection of vintage Minolta lenses as well. A full gear list is available upon request!",
              ],
            },
            {
              question: "WHAT KIND OF EDITING DO YOU DO FOR HEADSHOTS AND PORTRAITS?",
              answer: [
                "Look, we want you to look like you. We do the standard amount of retouching, we’ll get rid of that angry spot on the side of your nose or soften some of those laugh lines, maybe darken the backdrop for dynamics - but that’s about it. We want to maintain the integrity of your natural beauty as much as possible. Minimal editing is the name of our game.",
              ],
            },
            {
              question: "WHAT IN THE HECK DO I WEAR FOR A HEADSHOT SESSION?",
              answer: [
                "Wardrobe is always a hard one! For headshots, bring plenty of choices with contrast. Release the urge to pull all of your white T’s and black button-downs. Pops of color are fantastic but avoid logos or super busy patterns. This should be a curated collection of pieces that reflect you on one of your most stylish days. We invite you to pull options for what casting you’d like to be considered for; you can even send us any inspiration or moodboards in advance so we can collaborate with you on your vision!",
              ],
            },
            {
              question: "WHEN DO I GET MY EDITED PRINTS?",
              answer: [
                "We send the initial gallery of raw, unedited proofs within 48 hours of the shoot. We use PicTime as our photo sharing platform, so don’t worry we will 100% walk you through the process of choosing your faves or sharing your selects with your agent or whoever else you want to help you pick ‘em. Once you have picked the very best snaps of that stunning mug, your final edited prints will be sent to you. We endeavor to do that within 2 weeks of your shoot.",
              ],
            },
          ],
        },
        {
          id: "studio-hire",
          title: "Studio Hire",
          items: [
            {
              question: "PRICING",
              answer: [
                "Base package beginning at £70 per hour, 2-hour minimum",
                "Add-ons: cameras @ £25-£50 per day",
              ],
            },
            {
              question: "LIGHTS, CAMERA, ACTION? WHAT KIND OF GEAR IS AVAILABLE?",
              answer: [
                "We have a collection of constant and flash lighting for both photography and videography hire. We have two Godox SL60 constant lights, an Aperture 300D constant light and a Hawkeye LCD light. In terms of flash lighting, we use a Fujifilm flash trigger and our Godox speed light. All lights are modifiable with over a dozen softbox and umbrella diffusion options. These are all included in the price of hire.",
                "We also provide a varied and adaptable mix of backdrop options. We have both handpainted canvas backdrops and paper Colourama backdrops which can be put up as you wish with our modular Manfrotto autopole setup. These are also included in the price of hire.",
                "There is also the option of digital and film camera hire. We have a huge variety of beautiful vintage cameras and lenses which you can use in the studio. These are not included in the hire fee however so please enquire when booking and we can give you a run down of camera equipment and pricing.",
              ],
            },
          ],
        },
      ],
    };
  },
  methods: {
    isOpen(sectionId, index) {
      return !!this.openItems[`${sectionId}-${index}`];
    },
    toggle(sectionId, index) {
      const key = `${sectionId}-${index}`;
      this.openItems[key] = !this.openItems[key];
    },
    scrollToSection(sectionId) {
      this.sectionRefs[sectionId]?.scrollIntoView({ behavior: "smooth", block: "start" });
    },
  },
  mounted() {
    const elements = this.$el.querySelectorAll(".animated-element");
    const observer = new IntersectionObserver(
      (entries, observer) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("in-view");
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );

    elements.forEach((el) => observer.observe(el));
  },
};
</script>

<style scoped>
.container {
  background-color: var(--color-paper);
  width: 100%;
  color: black;
  line-height: 1.6;
}

.faq-inner {
  max-width: 720px;
  margin: 0 auto;
}

.page-title {
  font-family: "brown-sugar";
  font-size: 50px;
  text-align: center;
  margin-bottom: 30px;
}

/* Section jump links */
.section-nav {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 10px;
  margin-bottom: 50px;
}

.section-nav-item {
  font-family: "haffer-light";
  font-size: 14px;
  background: transparent;
  border: 1px solid black;
  border-radius: 999px;
  padding: 6px 16px;
  cursor: pointer;
  color: black;
  transition: background-color 0.3s ease, color 0.3s ease, border-color 0.3s ease;
}

.section-nav-item:hover {
  background-color: var(--button-hover-bg);
  color: var(--button-hover-text);
}

.faq-section {
  margin-bottom: 60px;
  scroll-margin-top: calc(var(--nav-height) + 20px);
}

.section-title {
  font-family: "brown-sugar";
  font-size: 28px;
  margin-bottom: 10px;
}

/* Accordion */
.faq-item {
  border-bottom: 1px solid rgba(0, 0, 0, 0.2);
}

.faq-question {
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 20px;
  padding: 20px 0;
  background: none;
  border: none;
  cursor: pointer;
  text-align: left;
  font-family: "haffer-light";
  font-size: 16px;
  letter-spacing: 0.03em;
  color: black;
  transition: color 0.3s ease;
}

.faq-question:hover,
.faq-item.open .faq-question {
  color: var(--color-maroon);
}

/* Plus icon that turns into a minus when open */
.faq-icon {
  position: relative;
  flex-shrink: 0;
  width: 14px;
  height: 14px;
}

.faq-icon::before,
.faq-icon::after {
  content: "";
  position: absolute;
  top: 50%;
  left: 0;
  width: 100%;
  height: 1px;
  background-color: currentColor;
  transition: transform 0.4s ease;
}

.faq-icon::after {
  transform: rotate(90deg);
}

.faq-item.open .faq-icon::after {
  transform: rotate(0deg);
}

/* Grid row trick animates height to/from auto */
.faq-answer {
  display: grid;
  grid-template-rows: 0fr;
  transition: grid-template-rows 0.4s ease;
}

.faq-item.open .faq-answer {
  grid-template-rows: 1fr;
}

.faq-answer-inner {
  overflow: hidden;
  opacity: 0;
  transform: translateY(-6px);
  transition: opacity 0.3s ease, transform 0.4s ease;
}

.faq-item.open .faq-answer-inner {
  opacity: 1;
  transform: translateY(0);
  transition-delay: 0.1s;
}

.faq-answer-inner > :last-child {
  margin-bottom: 20px;
}

.faq-answer p,
.faq-answer ul {
  font-size: 15px;
  margin-bottom: 12px;
}

.faq-answer ul {
  padding-left: 18px;
}

.faq-answer li {
  list-style: disc;
  margin-bottom: 4px;
}

.answer-heading {
  font-weight: bold;
  margin: 16px 0 6px;
}

.faq-answer :deep(strong) {
  font-weight: bold;
}

.faq-answer :deep(a) {
  color: black;
  text-decoration: underline;
  transition: color 0.3s ease;
}

.faq-answer :deep(a:hover) {
  color: palevioletred;
}

/* Animation styles */
.animated-element {
  opacity: 0;
  transform: translateY(20px);
  transition: opacity 0.6s ease, transform 0.6s ease;
}

.animated-element.in-view {
  opacity: 1;
  transform: translateY(0);
}

@media (prefers-reduced-motion: reduce) {
  .animated-element,
  .faq-answer,
  .faq-answer-inner,
  .faq-icon::before,
  .faq-icon::after {
    transition: none;
  }
}
</style>
